import { Grid, Link } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { HeaderGlobals } from '../../../globals/context'
import { isNotNull, nonNull } from '../../../utils/nonNull'
import { MobileMenu } from '../../MobileMenu/MobileMenu'
import {
  MobileMenuBackItem,
  MobileMenuHeader,
  MobileMenuLinkItem,
  MobileMenuUtilityItem,
} from '../../MobileMenu/MobileMenuItem'
import { StyledMobileMenuUtility } from '../../MobileMenu/styledMobileMenuUtility'
import { NavDropdownLinks } from '../../NavigationDropdown/NavDropdownLinks'
import { NavDropdownPanel } from '../../NavigationDropdown/NavDropdownPanel'
import { NavDropdownUtilityBar } from '../../NavigationDropdown/NavDropdownUtilityBar'
import {
  StyledHeaderContainer,
  StyledIconGroup,
  StyledOverviewContainer,
  StyledOverviewTitle,
} from '../../NavigationDropdown/styledNavDropdownUtilityBar'
import { NavItem } from '../../PrimaryNavigation/NavItem'
import { NavMenu } from '../../PrimaryNavigation/NavMenu'
import { PrimaryNavigation } from '../../PrimaryNavigation/PrimaryNavigation'
import { CreditUnionIcon, IconCodeType } from '../../icon/CreditUnionIcon'

export type HeaderProps = HeaderGlobals

const ICON_GROUP_TYPE = 'modules\\megaMenu\\nodes\\IconGroup' as const

export const HeaderFragment: React.VFC<HeaderProps> = ({ nodes, siteInfo }) => {
  const [open, setOpen] = useState(false)
  const router = useRouter()
  const secondary = useMemo(() => router.pathname !== '/', [router])
  const toggleMenu = useCallback(() => setOpen(!open), [setOpen, open])
  const closeMenu = useCallback(() => setOpen(false), [setOpen])
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  let idx = 0

  const { siteNumberBsb, siteNumberPhone, siteLoginLink } = useMemo(() => {
    if (siteInfo?.__typename === 'siteSettings_GlobalSet') {
      return siteInfo
    }

    return {}
  }, [siteInfo])

  useEffect(() => {
    if (!isMobile && open) {
      toggleMenu()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, open])

  if (!nodes || !siteInfo) {
    return null
  }

  return (
    <StyledHeaderContainer maxWidth="xl">
      <PrimaryNavigation BSB={siteNumberBsb ?? ''} LoginLink={siteLoginLink ?? ''} secondary={secondary} openMobileMenu={toggleMenu}>
        <NavMenu>
          {nonNull(nodes).map((node, i) => {
            const menus = node.children ? nonNull(node.children).filter((node) => node.type !== ICON_GROUP_TYPE) : []
            const iconGroup = node.children ? nonNull(node.children).find((node) => node.type === ICON_GROUP_TYPE) : undefined

            return (
              <NavItem
                key={i}
                title={node.title}
                aria-haspopup={node.children ? 'true' : 'false'}
                href={node.children && node.children.length > 0 ? '#' : node.url ?? '#'}
              >
                {menus.length > 0 ? (
                  <NavDropdownPanel title={node.title ?? ''}>
                    <Grid item>
                      <NavDropdownLinks container>
                        {
                          menus.map((node, i) => (
                            <Grid sm={6} md={3} item key={i}>
                              <NavDropdownLinks card target={node.newWindow === '1'} title={node.title ?? ''} href={node.url || ''}>
                                {node.children?.map((node, i) => (
                                  <NavDropdownLinks item target={node?.newWindow === '1'} key={i} href={node?.url ?? '#'}>
                                    {node?.title}
                                  </NavDropdownLinks>
                                ))}
                              </NavDropdownLinks>
                            </Grid>
                          ))
                        }
                        <StyledIconGroup key={i} item sm={6} md={3} container direction="column" justifyContent={iconGroup ? 'flex-start' : 'flex-end'}>
                          {iconGroup && (
                            <NavDropdownUtilityBar
                              title={iconGroup.title ?? ''}
                              href={iconGroup.url || '#'}
                              container
                              direction="column"
                              justifyContent="flex-start"
                              alignItems="flex-start">
                              {iconGroup?.children ? nonNull(iconGroup.children).map((node, i) => {
                                let parsedData: { icon?: string } = {}

                                try {
                                  parsedData = typeof node.data === 'string' ? JSON.parse(node.data) : node.data
                                } catch (err) {
                                  console.error('Failed to parse node.data:', node.data, err)
                                }

                                return (
                                  <NavDropdownUtilityBar
                                    card
                                    key={i}
                                    title={node?.title ?? ''}
                                    iconCode={parsedData.icon as IconCodeType}
                                    href={node?.url || '#'}
                                    target={node?.newWindow === '1'}
                                  />
                                )
                              }) : null}
                            </NavDropdownUtilityBar>
                          )}
                          <StyledOverviewContainer>
                            {/* @ts-ignore */}
                            {isNotNull(node.url) && node.url != '' && <Link target={node.newWindow === '1' ? '_blank' : '_self'} href={node.url} passHref>
                              <StyledOverviewTitle tabIndex={0}>
                                {(node.__typename === 'primaryNavigation_Node' && isNotNull(node.altOverviewPageLink)) ? node.altOverviewPageLink : `${node.title} overview page`}
                              </StyledOverviewTitle>
                            </Link>
                            }
                          </StyledOverviewContainer>
                        </StyledIconGroup>
                      </NavDropdownLinks>
                    </Grid>
                  </NavDropdownPanel>
                ) : null}
              </NavItem>
            )
          })}
        </NavMenu>
      </PrimaryNavigation>
      <MobileMenu wrapper portal={'#mobile-menu'} isMenuOpen={open}>
        <MobileMenu navigation level={0} idx={idx++}>
          <MobileMenuHeader phoneNumber={siteNumberPhone?.url ?? ''} bsbNumber={siteNumberBsb ?? ''} onClose={closeMenu} />
          {nonNull(nodes).map((node, i) => {
            const menus = node.children ? nonNull(node.children).filter((node) => node.type !== ICON_GROUP_TYPE) : []
            const iconGroup = node.children ? nonNull(node.children).find((node) => node.type === ICON_GROUP_TYPE) : undefined
            const title = node.__typename === 'primaryNavigation_Node' && isNotNull(node.altOverviewPageLink) ? node.altOverviewPageLink : `${node.title} overview`

            return (
              <MobileMenu item key={i} name={node.title ?? ''} level={0} idx={idx++} hasChildren={menus.length > 0}>
                <MobileMenu navigation level={1} idx={idx}>
                  <MobileMenuBackItem level={1} idx={idx}>Back</MobileMenuBackItem>
                  <MobileMenuLinkItem setClosed={closeMenu} href={node.url || '#'} isTitle>
                    {title}
                  </MobileMenuLinkItem>
                  {menus.length > 0 && menus.map((node, i) => (
                    <MobileMenu key={i} item name={node.title ?? ''} level={1} idx={idx++} hasChildren={Boolean(node.children && node.children.length > 0)}>
                      <MobileMenu navigation level={2} idx={idx}>
                        <MobileMenuBackItem level={2} idx={idx}>
                          {title}
                        </MobileMenuBackItem>
                        <MobileMenuLinkItem target={node.newWindow === '1'} setClosed={closeMenu} href={node.url || '#'} isTitle>
                          {node.title}
                        </MobileMenuLinkItem>
                        {node.children && nonNull(node.children).map((node, i) => (
                          <MobileMenuLinkItem target={node.newWindow === '1'} setClosed={closeMenu} key={i} href={node.url || '#'}>
                            {node.title}
                          </MobileMenuLinkItem>
                        ))}
                      </MobileMenu>
                    </MobileMenu>
                  ))}
                  {iconGroup && iconGroup.children && (
                    <StyledMobileMenuUtility>
                      {nonNull(iconGroup.children).map((node, i) => {
                        const parsedData = typeof node.data === 'string' ? JSON.parse(node.data) : node.data

                        return (
                          <MobileMenuUtilityItem target={node.newWindow === '1'} setClosed={closeMenu} key={i} href={node.url || '#'} icon={<CreditUnionIcon width={26} height={26} iconCode={parsedData.icon as IconCodeType} />}>
                            {node.title ?? ''}
                          </MobileMenuUtilityItem>
                        )
                      })}
                    </StyledMobileMenuUtility>
                  )}
                </MobileMenu>
              </MobileMenu>
            )
          })}
          <MobileMenuLinkItem item target={false} href="/help-and-support/contact-us">
            Contact
          </MobileMenuLinkItem>
        </MobileMenu>
      </MobileMenu>
    </StyledHeaderContainer>
  )
}